import { useEffect, useState } from 'react';
import {
  AllDocumentTypes,
  IPrismicService,
  MultiStepFormDocument,
} from '@monofront/types';
import { isFilled } from '@prismicio/helpers';
import {
  ICollection,
  MultiStepFormSliceDocumentDataBodySlice,
} from '@monofront/types';
import { toPascalCase } from '../utils';
import { MultiStepForm, MultiStepFormStep } from '../stateMachines';
import invariant from 'tiny-invariant';

export const useFirstOrderForm = (
  prismicDocumentId: string,
  usePrismic: () => { getDocumentByUid: IPrismicService['getDocumentByUid'] },
) => {
  const [content, setContent] = useState<null | MultiStepForm>(null);
  const [isLoading, setIsLoading] = useState(!content);
  const { getDocumentByUid } = usePrismic();

  useEffect(() => {
    (async function fetchData() {
      try {
        const content = await getDocumentByUid<MultiStepFormDocument>(
          'multi_step_form',
          prismicDocumentId,
          undefined,
          {
            fetchLinks: [
              'multi_step_form_slice.header_type',
              'multi_step_form_slice.is_header_bottom_border_curved',
              'multi_step_form_slice.body',
            ],
          },
        );

        const parsedContent = validatePrismicContent(content);

        setContent(parsedContent);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return {
    isLoading,
    content,
  };
};

/**
 * Validate the Prismic document and map it into a ready to consume object.
 */
export const validatePrismicContent = (
  content: MultiStepFormDocument | null,
) => {
  if (content == null) {
    throw new Error('The document was not found.');
  }

  if (!isFilled.group(content.data.steps)) {
    throw new Error('The steps field is not a group.');
  }

  const form = {
    steps: Object.fromEntries([
      ['page_loader', { uid: 'page_loader' } as MultiStepFormStep],
      ...content.data.steps.map((step) => {
        if (!isFilled.contentRelationship(step.step)) {
          throw new Error('The linked step document is not a document.');
        }

        const stepLink = step.step as typeof step.step & {
          data: Extract<
            AllDocumentTypes,
            { type: 'multi_step_form_slice' }
          >['data'];
        };

        if (stepLink.data.body.length !== 1) {
          throw new Error(
            'The step document must have exactly one body slice.',
          );
        }

        if (stepLink.data.body.length !== 1) {
          throw new Error(
            'The step document must have exactly one body slice.',
          );
        }

        const stepSlice = stepLink.data.body[0];

        invariant(stepSlice.variation === 'default');

        const { primary } = stepSlice;
        const stepData = validateStepData(stepSlice);

        return [
          stepLink.uid,
          {
            id: stepLink.id,
            uid: stepLink.uid,
            ...stepData,
            nextCTAText:
              'next_step_cta' in primary ? primary.next_step_cta : null,
            closeCTAText:
              'close_form_cta' in primary ? primary.close_form_cta : null,
            style: {
              container: {
                backgroundColor:
                  'background_color' in primary
                    ? primary.background_color
                    : null,
              },
              bottomInset: {
                backgroundColor:
                  'background_color' in primary
                    ? primary.background_color
                    : null,
              },
            },
            header: {
              type: stepLink.data.header_type,
              isBottomBorderCurved:
                stepLink.data.is_header_bottom_border_curved,
              image:
                'image' in primary && isFilled.image(primary.image)
                  ? primary.image
                  : null,
              style: {
                backgroundColor:
                  'header_background_color' in primary
                    ? primary.header_background_color
                    : null,
              },
            },
            title: isFilled.richText(primary.title) ? primary.title : null,
            subtitle:
              'subtitle' in primary && isFilled.richText(primary.subtitle)
                ? primary.subtitle
                : null,
            body: stepLink.data.body,
          } as MultiStepFormStep,
        ];
      }),
    ]),
  };

  return form;
};

/**
 * Ensure every slice field is filled and map them.
 */
const validateStepData = (step: MultiStepFormSliceDocumentDataBodySlice) => {
  switch (step.slice_type) {
    case 'MultiSelectCollection': {
      return {
        type: toPascalCase(step.slice_type),
        collections: step.items.map(({ collection, image, title }) => {
          if (!isFilled.integrationFields(collection)) {
            throw new Error('The linked collection document is not filled.');
          }

          if (!isFilled.image(image)) {
            throw new Error('The image for the collection is not filled.');
          }

          if (!isFilled.keyText(title)) {
            throw new Error('The text for the collection is not filled.');
          }

          return { collection: collection as ICollection, image, title };
        }),
      };
    }

    default: {
      return {
        type: toPascalCase(step.slice_type),
      };
    }
  }
};
